import { getUrlParam, getCookie, setCookie } from '@/utils/pub-use'
import { getDomainInfo } from '@/api/index'

declare global {
  interface Window {
    fbq?: any
    _fbq?: any
    DEFAULT_PIXEL_ID?: string
  }
}

/**
 * 初始化 Facebook Pixel
 * 优先级：URL参数 > 接口 > 缓存 > 默认值
 */
export async function initFacebookPixel() {
  try {
    // 优先从 URL 参数获取 pixelId
    const urlPixelId = getUrlParam("pixel")

    // 如果没有，尝试从接口获取
    let apiPixelId = null
    if (!urlPixelId) {
      try {
        const searchParams = new URLSearchParams(window.location.search)
        const channelId = searchParams.get('channelId') || ''
        const from = searchParams.get('huiohuhhuhiuhh') || ''
        const cachedFrom = getCookie('domain_from') || ''
        
        let domain = from || cachedFrom || window.location.hostname
        
        const res = await getDomainInfo({ params: { channelId, domain } })
        if (res.code === 0) {
          apiPixelId = res.data?.channelInfo?.trackConfig?.facebook?.pixelId
          // 如果从接口获取到了，保存到 cookie
          if (apiPixelId) {
            setCookie('facebook_pixel_id', apiPixelId)
          }
        }
      } catch (error) {
        console.warn('[FB Pixel] 获取接口配置失败:', error)
      }
    }
    
    // 如果还没有，从 cookie 获取
    const cachedPixelId = getCookie('facebook_pixel_id')
    
    // 如果从 URL 获取到了，更新 cookie
    if (urlPixelId) {
      setCookie('facebook_pixel_id', urlPixelId)
    }
    
    // 优先级：URL > 接口 > 缓存 > 默认值
    const pixelId = urlPixelId || apiPixelId || cachedPixelId || window.DEFAULT_PIXEL_ID

    if (!pixelId) {
      console.warn('[FB Pixel] Facebook Pixel ID not found')
      return false
    }

    // 加载 Facebook Pixel SDK
    ;(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");

    // 初始化 Facebook Pixel
    window.fbq("init", pixelId);
    window.fbq("track", "PageView");

    // 动态创建 noscript 标签
    // 先移除旧的 noscript 标签（如果存在）
    const existingNoscript = document.querySelector('noscript img[src*="facebook.com/tr"]')
    if (existingNoscript && existingNoscript.parentElement) {
      existingNoscript.parentElement.remove()
    }

    const createNoscript = () => {
      const noscriptImg = document.createElement('img');
      noscriptImg.height = 1;
      noscriptImg.width = 1;
      noscriptImg.style.display = 'none';
      noscriptImg.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;

      const noscript = document.createElement('noscript');
      noscript.appendChild(noscriptImg);
      document.head.appendChild(noscript);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', createNoscript);
    } else {
      createNoscript();
    }

    return true
  } catch (error) {
    console.error('[FB Pixel] 初始化失败:', error);
    return false
  }
}

/**
 * 检查 Facebook Pixel 是否已初始化
 */
export function isFacebookPixelInitialized(): boolean {
  return !!(window.fbq && window.fbq.loaded)
}

/**
 * 上报 Facebook Pixel 事件
 * @param eventName 事件名称
 * @param params 事件参数
 */
export function trackFacebookEvent(eventName: string, params?: Record<string, any>) {
  try {
    // 优先从 URL 参数获取 pixelId
    const urlPixelId = getUrlParam("pixel")
    const cachedPixelId = getCookie('facebook_pixel_id')
    const defaultPixelId = window.DEFAULT_PIXEL_ID || null
    const pixelId = urlPixelId || cachedPixelId || defaultPixelId

    // 如果从 URL 获取到了 pixelId，确保使用它
    if (urlPixelId && (!window.fbq || !window.fbq.loaded)) {
      // 如果 URL 中有 pixelId 但未初始化，重新初始化
      initFacebookPixel()
    }

    if (typeof window.fbq === 'function') {
      if (pixelId) {
        // 使用 trackSingle 方法，显式传递 pixelId，确保使用 URL 中的 pixelId
        if (params) {
          window.fbq('trackSingle', pixelId, eventName, params)
          console.log(`[FB Pixel] 上报事件: ${eventName}`, { pixelId, params })
        } else {
          window.fbq('trackSingle', pixelId, eventName)
          console.log(`[FB Pixel] 上报事件: ${eventName}`, { pixelId })
        }
      } else {
        // 如果没有 pixelId，使用标准的 track 方法
        if (params) {
          window.fbq('track', eventName, params)
          console.log(`[FB Pixel] 上报事件: ${eventName}`, params)
        } else {
          window.fbq('track', eventName)
          console.log(`[FB Pixel] 上报事件: ${eventName}`)
        }
      }
    } else {
      console.warn(`[FB Pixel] fbq 未初始化，无法上报事件: ${eventName}`)
    }
  } catch (error) {
    console.error(`[FB Pixel] 上报事件失败 ${eventName}:`, error)
  }
}

/**
 * 上报自定义事件
 * @param eventName 事件名称
 * @param params 事件参数
 */
export function trackFacebookCustomEvent(eventName: string, params?: Record<string, any>) {
  try {
    if (typeof window.fbq === 'function') {
      if (params) {
        window.fbq('trackCustom', eventName, params)
      } else {
        window.fbq('trackCustom', eventName)
      }
    }
  } catch (error) {
    console.error(`[FB Pixel] 上报自定义事件失败 ${eventName}:`, error)
  }
}

/**
 * 上报注册完成事件
 */
export function trackCompleteRegistration(params?: Record<string, any>) {
  trackFacebookEvent('CompleteRegistration', params)
}

/**
 * 上报注册点击事件
 */
export function trackRegisterClick(params?: Record<string, any>) {
  trackFacebookEvent('RegisterClick', params)
}

/**
 * 上报充值事件
 * @param amount 充值金额
 * @param currency 货币类型
 * @param isFirst 是否首充
 */
export function trackRecharge(amount: number, currency: string = 'INR', isFirst: boolean = false) {
  const params = {
    value: amount,
    currency
  }
  
  if (isFirst) {
    // 首充使用 Purchase 事件
    trackFacebookEvent('Purchase', params)
  } else {
    // 复充使用 Recharge 事件
    trackFacebookEvent('Recharge', params)
  }
}

/**
 * 上报充值点击事件（复充）
 */
export function trackRechargeClick(params?: Record<string, any>) {
  trackFacebookEvent('RechargeClick', params)
}

/**
 * 上报首充点击事件（PurchaseClick）
 */
export function trackPurchaseClick(params?: Record<string, any>) {
  trackFacebookEvent('PurchaseClick', params)
}

/**
 * 上报页面浏览事件
 */
export function trackPageView(params?: Record<string, any>) {
  trackFacebookEvent('PageView', params)
}

/**
 * 检查 Facebook Pixel 上报状态（可在控制台调用）
 * 查看所有已上报的事件
 */
export function checkFacebookPixelEvents() {
  try {
    const pixelId = getCookie('facebook_pixel_id') || window.DEFAULT_PIXEL_ID
    const isInitialized = !!(window.fbq && window.fbq.loaded)
    
    console.group('🔍 Facebook Pixel 事件检查')
    console.log('✅ Pixel ID:', pixelId || '未找到')
    console.log('✅ 是否已初始化:', isInitialized ? '是' : '否')
    
    if (window.fbq) {
      console.log('✅ fbq.loaded:', window.fbq.loaded)
      console.log('✅ fbq.version:', window.fbq.version)
      console.log('✅ fbq.queue:', window.fbq.queue)
    }
    
    // 检查网络请求中的事件
    const events: Array<{ event: string; url: string; time: string }> = []
    if (performance && performance.getEntriesByType) {
      const entries = performance.getEntriesByType('resource')
      entries.forEach(entry => {
        if (entry.name && entry.name.includes('facebook.com/tr')) {
          // 解析事件名称
          let eventName = 'PageView'
          if (entry.name.includes('ev=CompleteRegistration')) {
            eventName = 'CompleteRegistration'
          } else if (entry.name.includes('ev=Purchase')) {
            eventName = 'Purchase'
          } else if (entry.name.includes('ev=Recharge')) {
            eventName = 'Recharge'
          } else if (entry.name.includes('ev=RegisterClick')) {
            eventName = 'RegisterClick'
          } else if (entry.name.includes('ev=RechargeClick')) {
            eventName = 'RechargeClick'
          }
          
          events.push({
            event: eventName,
            url: entry.name,
            time: new Date(entry.startTime + entry.duration).toLocaleTimeString()
          })
        }
      })
    }
    
    if (events.length > 0) {
      console.log('✅ 已上报的事件:')
      events.forEach(e => {
        console.log(`   - ${e.event} (${e.time})`)
      })
    } else {
      console.log('⚠️ 未检测到事件上报，请检查：')
      console.log('   1. 打开浏览器开发者工具 -> Network 面板')
      console.log('   2. 筛选 "facebook.com" 或 "tr"')
      console.log('   3. 执行注册或充值操作')
      console.log('   4. 查看是否有新的请求')
    }
    
    console.groupEnd()
    
    return {
      pixelId,
      isInitialized,
      events,
      fbq: window.fbq
    }
  } catch (error) {
    console.error('检查 Facebook Pixel 事件时出错:', error)
    return null
  }
}

// 将检查函数挂载到 window 对象，方便在控制台调用
if (typeof window !== 'undefined') {
  (window as any).checkFacebookPixelEvents = checkFacebookPixelEvents
}

